'use client';

import { FC, useMemo } from 'react';

import {
  EMetric,
  metricDictionary,
  StatsResult,
  TrendsResult
} from 'api/useAnalytics';
import { DATE_DAY_MONTH_FORMAT } from 'constants/constants';
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';
import { EStatisticType } from 'views/Statistics/StatisticsView.types';

import { Card, CardContent, CardHeader } from 'components/ui/card';
import { ChartContainer, ChartTooltip } from 'components/ui/chart';

import { AnalyticsBoxHeader } from './AnalyticsBoxHeader/AnalyticsBoxHeader';
import { chartConfig } from './analyticsChart.config';
import { AnalyticsChartTooltip } from './AnalyticsChartTooltip/AnalyticsChartTooltip';
import { AnalyticsLoadingBox } from './AnalyticsLoadingBox';
import {
  getChartLabelFromDate,
  getFormattedTrendsData,
  getFormattedValueByType
} from './utils';

type AnalyticsBoxProps = {
  isLoading: boolean;
  metricName: EMetric;
  stat?: StatsResult;
  currentTrends: TrendsResult['values'];
  previousTrends?: TrendsResult['values'];
  xAxisDateFormat?: string;
};

export const AnalyticsBox: FC<AnalyticsBoxProps> = ({
  isLoading,
  metricName,
  stat,
  currentTrends,
  previousTrends,
  xAxisDateFormat = DATE_DAY_MONTH_FORMAT
}) => {
  const chartData = useMemo(
    () => getFormattedTrendsData(currentTrends, previousTrends),
    [currentTrends, previousTrends]
  );

  if (isLoading) return <AnalyticsLoadingBox />;

  const { title } = metricDictionary[metricName];
  const metricType = metricDictionary[metricName].type ?? EStatisticType.NUMBER;
  const tickStyle = {
    fill: 'var(--base-muted-foreground)',
    fontSize: '10px',
    fontFamily: 'var(--typography-font-family-font-sans, Inter)',
    fontStyle: 'normal',
    fontWeight: 'var(--font-weight-normal, 400)'
  };

  return (
    <Card className="p-[24px] shadow-none">
      <CardHeader className="p-0">
        <AnalyticsBoxHeader stat={stat} metricName={metricName} />
      </CardHeader>
      <CardContent className="p-0 pt-[22px]">
        <ChartContainer
          config={chartConfig}
          className="min-h-[220px] max-h-[220px] w-full"
        >
          <LineChart margin={{ left: 0 }} data={chartData}>
            <CartesianGrid vertical={false} stroke="var(--base-border)" />
            <XAxis
              tickFormatter={(value) =>
                getChartLabelFromDate(value, xAxisDateFormat)
              }
              axisLine={false}
              tickLine={false}
              dataKey="currentDate"
              tick={tickStyle}
              dy={10}
            />
            <YAxis
              tickFormatter={(value) =>
                getFormattedValueByType(value, metricType)
              }
              tick={tickStyle}
              axisLine={false}
              tickLine={false}
              tickMargin={10}
            />
            <ChartTooltip
              content={
                <AnalyticsChartTooltip title={title} type={metricType} />
              }
              wrapperStyle={{ outline: 'none', border: 'none' }}
              cursor={{ stroke: 'var(--base-border)', strokeWidth: 2 }}
            />
            <Line
              type="monotone"
              dataKey="previousValue"
              stroke={chartConfig.previous.color}
              strokeWidth={1}
              dot={false}
              strokeDasharray="2 2"
              activeDot={{
                r: 4,
                stroke: 'var(--base-white)',
                strokeWidth: 1,
                fill: 'var(--base-chart-3)'
              }}
            />
            <Line
              type="monotone"
              dataKey="currentValue"
              stroke={chartConfig.current.color}
              strokeWidth={1}
              dot={false}
              activeDot={{
                r: 4,
                stroke: 'var(--base-white)',
                strokeWidth: 1,
                fill: chartConfig.current.color
              }}
            />
          </LineChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
};
